import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Card from "@components/cards/Card/Card";
import TextInput from "@components/inputs/TextInput/TextInput";
import styles from "@styles/CommonEditorPageLayout.module.css";
import MediaSelector from "@components/inputs/MediaSelector/MediaSelector";
import DateInput from "@components/inputs/DateInput/DateInput";
import TimeInput, { TimeValue } from "@components/inputs/TimeInput/TimeInput";
import LocationInput from "@components/inputs/LocationInput/LocationInput";
import { isRequiredField, isValidMaxLength, isValidUrl } from "@util/validators";
import CheckboxWithLabel from "@components/inputs/Checkbox/CheckboxWithLabel";
import UnsavedChangesPrompt from "@components/misc/UnsavedChangesPrompt";
import EditorCompleteButtons from "@components/buttons/EditorCompleteButtons/EditorCompleteButtons";
import {
    MAX_MEMORIAL_SERVICE_INFORMATION_LENGTH,
    MAX_MEMORIAL_SERVICE_LOCATION_LENGTH,
    MAX_MEMORIAL_SERVICE_NAME_LENGTH,
    MAX_MEMORIAL_SERVICE_TYPE_LENGTH,
} from "@constants/validation";
import { createUpcomingService, fetchUpcomingService, updateUpcomingService } from "@services/upcomingServiceService";
import { MediaFile } from "@models/Common";
import { UpcomingServiceUpdate } from "@models/UpcomingService";
import { delay } from "@util/time";
import Button from "@components/buttons/Button/Button";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { ServiceUpdate } from "@models/Service";
import { AxiosProgressEvent } from "axios";
import TimezoneInput from "@components/inputs/TimezoneInput/TimezoneInput";

const MAX_SERVICES = 4;

const DEFAULT_SERVICE = { };

const UpcomingServiceEditorPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get("mode");
    const id = searchParams.get("id");

    const [loading, setLoading] = useState(mode === "edit");
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [changedFields, setChangedFields] = useState<UpcomingServiceUpdate>({});
    const [serviceCount, setServiceCount] = useState(0);

    const [image, setImage] = useState<MediaFile | null>(null);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [birthDate, setBirthDate] = useState<Date | null>(null);
    const [deathDate, setDeathDate] = useState<Date | null>(null);
    const [directCremation, setDirectCremation] = useState(false);
    const [privateService, setPrivateService] = useState(false);
    const [enableRsvp, setEnableRsvp] = useState(false);
    const [services, setServices] = useState<ServiceUpdate[]>([]);
    const [noIndex, setNoIndex] = useState(false);
    const [memorialId, setMemorialId] = useState<string | null>();

    const [imageError, setImageError] = useState("");
    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [birthDateError, setBirthDateError] = useState("");
    const [deathDateError, setDeathDateError] = useState("");
    const [serviceTypeError, setServiceTypeError] = useState("");
    const [locationError, setLocationError] = useState("");
    const [liveStreamLinkError, setLiveStreamLinkError] = useState("");
    const [informationError, setInformationError] = useState("");
    const [submitErrorMessage, setSubmitErrorMessage] = useState("");

    const hasUnsavedChanges = Object.keys(changedFields).length > 0;

    // Load service information in edit mode
    useEffect(() => {
        if (mode === "edit") {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    if (!id) {
                        throw new Error("No ID.");
                    }
                    const data = await fetchUpcomingService(id); // Fetch data from API
                    setFirstName(data.firstName);
                    setLastName(data.lastName);
                    setBirthDate(data.birthDate || null);
                    setDeathDate(data.deathDate || null);
                    setDirectCremation(!!data.directCremation);
                    setPrivateService(!!data.privateService);
                    setEnableRsvp(!!data.enableRsvp);
                    setServices(data.services);
                    setServiceCount(data.services.length);
                    setImage(data.image || null);
                    setNoIndex(data.noIndex);
                    setMemorialId(data.memorialId);
                } catch (err) {
                    setError("Failed to fetch service information.");
                } finally {
                    setLoading(false);
                }
            };
            void fetchData();
        }
    }, [mode, id]);

    // Validation functions
    const validateFirstName = (val: string) => {
        const result = isRequiredField(val, "First name");
        if (result) return result;
        return isValidMaxLength(val, MAX_MEMORIAL_SERVICE_NAME_LENGTH);
    };

    const validateLastName = (val: string) => {
        const result = isRequiredField(val, "Last name");
        if (result) return result;
        return isValidMaxLength(val, MAX_MEMORIAL_SERVICE_NAME_LENGTH);
    };

    const validateBirthDate = (val: Date | null) => {
        // return val ? "" : "Date of birth is required.";
        return "";
    };

    const validateDeathDate = (val: Date | null) => {
        // return val ? "" : "Date of death is required.";
        return "";
    };

    const validateServiceType = (val: string) => {
        if (val) {
            return isValidMaxLength(val, MAX_MEMORIAL_SERVICE_TYPE_LENGTH);
        }
        return "";
    };

    const validateLocation = (val: string) => {
        if (val) {
            return isValidMaxLength(val, MAX_MEMORIAL_SERVICE_LOCATION_LENGTH);
        }
        return "";
    };

    const validateLiveStreamLink = (val: string) => {
        if (val) {
            return isValidUrl(val);
        }
        return "";
    };

    const validateInformation = (val: string) => {
        if (val) {
            return isValidMaxLength(val, MAX_MEMORIAL_SERVICE_INFORMATION_LENGTH);
        }
        return "";
    };

    // Input handlers with validation
    const onFirstNameChange = useCallback((val: string) => {
        setFirstName(val);
        setFirstNameError(validateFirstName(val));
        setChangedFields((prev) => ({ ...prev, firstName: val }));
    }, []);

    const onLastNameChange = useCallback((val: string) => {
        setLastName(val);
        setLastNameError(validateLastName(val));
        setChangedFields((prev) => ({ ...prev, lastName: val }));
    }, []);

    const onBirthDateChange = useCallback((val: Date | null) => {
        setBirthDate(val);
        setBirthDateError(validateBirthDate(val));
        setChangedFields((prev) => ({ ...prev, birthDate: val || null }));
    }, []);

    const onDeathDateChange = useCallback((val: Date | null) => {
        setDeathDate(val);
        setDeathDateError(validateDeathDate(val));
        setChangedFields((prev) => ({ ...prev, deathDate: val || null }));
    }, []);

    const onServiceTypeChange = useCallback((index: number, val: string) => {
        setServices((prev) => {
            const newState = [...prev];
            newState[index].type = val;
            return newState;
        });
        setServiceTypeError(validateServiceType(val));
        setChangedFields((prev) => {
            const newState = { ...prev };
            const newServices = newState.services || [...services];

            if (!newServices[index]) {
                newServices.push({ ...DEFAULT_SERVICE });
            }

            newServices[index].type = val;
            newState.services = newServices;
            return newState;
        });
    }, [services]);

    const onServiceDateChange = useCallback((index: number, val: Date | null) => {
        if (val) {
            setServices((prev) => {
                const newState = [...prev];
                const prevDateTime = newState[index].dateTime;

                // Create a new UTC Date object using UTC methods only
                newState[index].dateTime = prevDateTime
                    ? new Date(
                        Date.UTC(
                            val.getUTCFullYear(),
                            val.getUTCMonth(),
                            val.getUTCDate(),
                            prevDateTime.getUTCHours(),
                            prevDateTime.getUTCMinutes(),
                        ),
                    )
                    : val;

                if (!services[index].timezone) {
                    // Default to users current timezone
                    newState[index].timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                }
                return newState;
            });
        }

        setChangedFields((prev) => {
            const newState = { ...prev };
            const newServices = newState.services || [...services];

            if (!newServices[index]) {
                newServices.push({ ...DEFAULT_SERVICE });
            }

            const prevDateTime = services[index].dateTime;
            newServices[index].dateTime = prevDateTime && val
                ? new Date(
                    Date.UTC(
                        val.getUTCFullYear(),
                        val.getUTCMonth(),
                        val.getUTCDate(),
                        prevDateTime.getUTCHours(),
                        prevDateTime.getUTCMinutes(),
                    ),
                )
                : val || undefined;

            if (!newServices[index].timezone) {
                // Default to users current timezone
                newServices[index].timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            }
            newState.services = newServices;
            return newState;
        });
    }, [services]);

    const onServiceTimeChange = useCallback((index: number, val: TimeValue | null) => {
        if (val) {
            setServices((prev) => {
                const newState = [...prev];
                const prevDateTime = newState[index].dateTime;

                const newDate = prevDateTime ? new Date(prevDateTime) : new Date();
                // Set hours and minutes using UTC methods to avoid local timezone issues
                newDate.setUTCHours(Number(val.hours), Number(val.minutes), 0, 0);
                newState[index].dateTime = prevDateTime ? newDate : undefined;
                return newState;
            });
        }

        setChangedFields((prev) => {
            const newState = { ...prev };
            const newServices = newState.services || [...services];

            if (!newServices[index]) {
                newServices.push(services[index]);
            }

            const prevDateTime = services[index].dateTime;
            const newDate = prevDateTime ? new Date(prevDateTime) : new Date();

            if (val) {
                // Set hours and minutes in UTC
                newDate.setUTCHours(Number(val.hours), Number(val.minutes), 0, 0);
            }

            newServices[index].dateTime = prevDateTime ? newDate : undefined;
            newState.services = newServices;
            return newState;
        });
    }, [services]);

    const onTimezoneChange = useCallback((index: number, val: string | null) => {
        setServices((prev) => {
            const newState = [...prev];
            if (val) {
                newState[index].timezone = val;
            } else {
                delete newState[index].timezone;
            }
            return newState;
        });
        setChangedFields((prev) => {
            const newState = { ...prev };
            const newServices = newState.services || [...services];

            if (!newServices[index]) {
                newServices.push({ ...DEFAULT_SERVICE });
            }

            if (val) {
                newServices[index].timezone = val;
            } else {
                delete newServices[index].timezone;
            }
            newState.services = newServices;
            return newState;
        });
    }, [services]);

    const onLocationChange = useCallback((index: number, val: string) => {
        setServices((prev) => {
            const newState = [...prev];
            newState[index].location = val;
            return newState;
        });
        setLocationError(validateLocation(val));
        setChangedFields((prev) => {
            const newState = { ...prev };
            const newServices = newState.services || [...services];

            if (!newServices[index]) {
                newServices.push(services[index]);
            }

            newServices[index].location = val;
            newState.services = newServices;
            return newState;
        });
    }, [services]);

    const onLiveStreamLinkChange = useCallback((index: number, val: string) => {
        setServices((prev) => {
            const newState = [...prev];
            newState[index].liveStreamLink = val;
            return newState;
        });
        setLiveStreamLinkError(validateLiveStreamLink(val));
        setChangedFields((prev) => {
            const newState = { ...prev };
            const newServices = newState.services || [...services];

            if (!newServices[index]) {
                newServices.push(services[index]);
            }

            newServices[index].liveStreamLink = val;
            newState.services = newServices;
            return newState;
        });
    }, [services]);

    const onInformationChange = useCallback((index: number, val: string) => {
        setServices((prev) => {
            const newState = [...prev];
            newState[index].information = val;
            return newState;
        });
        setInformationError(validateInformation(val));
        setChangedFields((prev) => {
            const newState = { ...prev };
            const newServices = newState.services || [...services];

            if (!newServices[index]) {
                newServices.push(services[index]);
            }

            newServices[index].information = val;
            newState.services = newServices;
            return newState;
        });
    }, [services]);

    const progressCallback = useCallback((progressEvent: AxiosProgressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        setUploadProgress(progress);
    }, []);

    // Save handler
    const handleSave = async () => {
        let isValid = true;

        if (imageError) isValid = false;

        // Validate First Name
        let errorMessage = validateFirstName(firstName);
        setFirstNameError(errorMessage);
        if (errorMessage) isValid = false;

        // Validate Last Name
        errorMessage = validateLastName(lastName);
        setLastNameError(errorMessage);
        if (errorMessage) isValid = false;

        // Validate Date of Birth
        errorMessage = validateBirthDate(birthDate);
        setBirthDateError(errorMessage);
        if (errorMessage) isValid = false;

        // Validate Date of Death
        errorMessage = validateDeathDate(deathDate);
        setDeathDateError(errorMessage);
        if (errorMessage) isValid = false;

        for (let i = 0; i < services.length; i++) {
            // Validate Service Type
            errorMessage = validateServiceType(services[i].type || "");
            setServiceTypeError(errorMessage);
            if (errorMessage) isValid = false;

            // Validate Location
            errorMessage = validateLocation(services[i].location || "");
            setLocationError(errorMessage);
            if (errorMessage) isValid = false;

            // Validate Live Stream Link
            errorMessage = validateLiveStreamLink(services[i].liveStreamLink || "");
            setLiveStreamLinkError(errorMessage);
            if (errorMessage) isValid = false;

            // Validate Information
            errorMessage = validateInformation(services[i].information || "");
            setInformationError(errorMessage);
            if (errorMessage) isValid = false;
        }

        if (isValid) {
            try {
                setIsSaving(true);
                setUploadProgress(0);
                setSubmitErrorMessage("");
                let createdId = "";

                if (mode === "add") {
                    createdId = await createUpcomingService(changedFields, progressCallback);
                } else {
                    if (!id) {
                        throw new Error("No ID.");
                    }
                    await updateUpcomingService(id, changedFields, progressCallback);
                }

                setChangedFields({});
                setIsSaving(false);
                await delay(1); // Wait for saving state to be cleared
                if (mode === "add") {
                    // Change to edit mode after creation
                    searchParams.set("mode", "edit");
                    searchParams.set("id", createdId);
                    navigate(`?${searchParams.toString()}`, { replace: true });
                }
            } catch (err) {
                const msg = "Failed to save service information. Please try again.";
                setSubmitErrorMessage(msg);
                alert(msg);
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleAddService = () => {
        setServiceCount((prevState) => prevState + 1);

        setServices((prevState) => {
            const newServices = [...prevState, { ...DEFAULT_SERVICE }];
            return newServices;
        });

        setChangedFields((prev) => {
            const prevServices = prev.services || services;
            const newServices = prevServices ? [...prevServices, { ...DEFAULT_SERVICE }] : [{ ...DEFAULT_SERVICE }];
            return { ...prev, services: newServices };
        });
    };

    const handleRemoveService = (index: number) => {
        if (serviceCount <= 0) return; // Prevent removing when no services are present

        setServiceCount((prevState) => prevState - 1);

        setServices((prevState) => {
            // Remove the service at the given index
            const newServices = prevState.filter((_, i) => i !== index);
            return newServices;
        });

        setChangedFields((prev) => {
            const prevServices = prev.services || services;
            const newServices = prevServices
                ? prevServices.filter((_, i) => i !== index)
                : [];
            return { ...prev, services: newServices };
        });
    };

    const renderContent = () => {
        if (loading || isSaving || error) {
            return (
                <LoadSaveErrorDisplay
                    isLoading={loading}
                    isSaving={isSaving}
                    progress={changedFields.image ? uploadProgress : undefined}
                    error={error}
                />
            );
        }

        const noServices = directCremation || privateService;

        return (
            <div className={styles.container}>
                <div className={styles.headerContainer}>
                    <h1>
                        {mode === "add" ? "Add" : "Edit"}
                        {" "}
                        Funeral Notice
                    </h1>
                </div>
                <div className={styles.buttonGroup}>
                    <EditorCompleteButtons
                        hasUnsavedChanges={hasUnsavedChanges}
                        isSaving={isSaving}
                        isNew={mode === "add"}
                        onSave={handleSave}
                    />
                </div>
                <div className={styles.contentContainer}>
                    <h2>About</h2>
                    <Card className={styles.cardContainer}>
                        {!memorialId && (
                            <MediaSelector
                                label="Select Image"
                                id="image"
                                media={image}
                                errorMessage={imageError}
                                onMediaChange={(img, err) => {
                                    setImage(img as MediaFile);
                                    setImageError(err);
                                    setChangedFields((prev) => ({ ...prev, image: img as MediaFile }));
                                }}
                                mediaTypeFilter="image"
                            />
                        )}
                        <TextInput
                            label="First Name *"
                            id="firstName"
                            value={firstName}
                            onChange={onFirstNameChange}
                            placeholder="Enter first name..."
                            errorMessage={firstNameError}
                            disabled={Boolean(memorialId)}
                        />
                        <TextInput
                            label="Last Name *"
                            id="lastName"
                            value={lastName}
                            onChange={onLastNameChange}
                            placeholder="Enter last name..."
                            errorMessage={lastNameError}
                            disabled={Boolean(memorialId)}
                        />
                        <DateInput
                            id="birthDate"
                            label="Date of Birth"
                            value={birthDate}
                            onChange={onBirthDateChange}
                            errorMessage={birthDateError}
                            disabled={Boolean(memorialId)}
                        />
                        <DateInput
                            id="deathDate"
                            label="Date of Death"
                            value={deathDate}
                            onChange={onDeathDateChange}
                            errorMessage={deathDateError}
                            disabled={Boolean(memorialId)}
                        />
                        <CheckboxWithLabel
                            id="directCremation"
                            label="Direct Cremation"
                            description="No service details will be shown on the public notice"
                            checked={directCremation}
                            onChange={(val) => {
                                setDirectCremation(val);
                                setChangedFields((prev) => ({
                                    ...prev,
                                    directCremation: val,
                                    ...(val ? { privateService: false } : {}),
                                }));
                                if (val) {
                                    setPrivateService(false);
                                }
                            }}
                        />
                        <CheckboxWithLabel
                            id="privateService"
                            label="Private Service"
                            description="No service details will be shown on the public notice; will display as 'Private Service'"
                            checked={privateService}
                            onChange={(val) => {
                                setPrivateService(val);
                                setChangedFields((prev) => ({
                                    ...prev,
                                    privateService: val,
                                    ...(val ? { directCremation: false } : {}),
                                }));
                                if (val) {
                                    setDirectCremation(false);
                                }
                            }}
                        />
                        <CheckboxWithLabel
                            id="enableRsvp"
                            label="Enable RSVP"
                            checked={enableRsvp}
                            onChange={(val) => {
                                setEnableRsvp(val);
                                setChangedFields((prev) => ({ ...prev, enableRsvp: val }));
                            }}
                        />
                        {!memorialId && (
                            <CheckboxWithLabel
                                id="indexable"
                                label="Appear In Search"
                                description="Viewable in Notices List and Google Search"
                                checked={!noIndex}
                                onChange={(val) => {
                                    setNoIndex(!val);
                                    setChangedFields((prev) => ({ ...prev, noIndex: !val }));
                                }}
                            />
                        )}
                        {memorialId && (
                            <div className={styles.note}>
                                <p>Information and image can be edited via the linked memorial
                                    {" "}
                                    <Link to={`/memorials/editor?mode=edit&id=${memorialId}`} className={styles.link}>
                                        here
                                    </Link>
                                    .
                                </p>
                            </div>
                        )}
                    </Card>
                </div>
                <div className={styles.contentContainer}>
                    {(serviceCount > 0 && !noServices) && (
                        <h2>Service</h2>
                    )}
                    <div className={styles.cardGrid}>
                        {!noServices && new Array(serviceCount).fill(0).map((_, index) => (
                            <Card key={index} className={styles.cardContainer}>
                                <TextInput
                                    label="Service Type"
                                    id="type"
                                    value={services[index].type || ""}
                                    onChange={(val) => {
                                        onServiceTypeChange(index, val);
                                    }}
                                    placeholder="eg. Service, Mass, Rosary, Burial etc."
                                    errorMessage={serviceTypeError}
                                />
                                <DateInput
                                    id="serviceDate"
                                    label="Date of Service"
                                    value={services[index].dateTime || null}
                                    onChange={(val) => {
                                        onServiceDateChange(index, val);
                                    }}
                                />
                                <TimeInput
                                    id="serviceTime"
                                    label="Service Time"
                                    disabled={!services[index].dateTime}
                                    value={
                                        services[index].dateTime
                                            ? {
                                                hours: String(services[index].dateTime?.getUTCHours()).padStart(2, "0"),
                                                minutes: String(services[index].dateTime?.getUTCMinutes()).padStart(2, "0"),
                                            }
                                            : null
                                    }
                                    onChange={(val) => {
                                        onServiceTimeChange(index, val);
                                    }}
                                />
                                <TimezoneInput
                                    id="timezone-input"
                                    label="Select Timezone"
                                    disabled={!services[index].dateTime}
                                    value={services[index].timezone || null}
                                    onChange={(val) => {
                                        onTimezoneChange(index, val);
                                    }}
                                />
                                <TextInput
                                    id="location"
                                    label="Service Location"
                                    description="Note: To ensure Google Maps location displays correctly, include full address"
                                    value={services[index].location || ""}
                                    onChange={(val) => {
                                        onLocationChange(index, val);
                                    }}
                                    placeholder="Enter service location..."
                                    errorMessage={locationError}
                                />
                                <TextInput
                                    label="Live Stream Link"
                                    id="liveStreamLink"
                                    value={services[index].liveStreamLink || ""}
                                    onChange={(val) => {
                                        onLiveStreamLinkChange(index, val);
                                    }}
                                    placeholder="Enter live stream url..."
                                    errorMessage={liveStreamLinkError}
                                />
                                <TextInput
                                    label="Information"
                                    id="information"
                                    value={services[index].information || ""}
                                    onChange={(val) => {
                                        onInformationChange(index, val);
                                    }}
                                    placeholder="Enter additional information (eg. in lieu of flowers, livestream password, private event etc)..."
                                    multiline
                                    height="150px"
                                    errorMessage={informationError}
                                />
                                <Button onClick={() => handleRemoveService(index)} variant="secondary">
                                    Remove
                                </Button>
                            </Card>
                        ))}
                    </div>
                    {(serviceCount > 1 && !noServices) && (
                        <div className={styles.note}>
                            <p>Services will automatically order chronologically.</p>
                        </div>
                    )}
                    {noServices && (
                        <div className={styles.note}>
                            <p>Services cannot be added when direct cremation or private service is selected.</p>
                        </div>
                    )}
                    {serviceCount < MAX_SERVICES && (
                        <div className={styles.addButton}>
                            <Button onClick={handleAddService} disabled={noServices}>
                                {`Add ${serviceCount > 0 ? "Another " : ""}Service`}
                            </Button>
                        </div>
                    )}
                </div>
                {submitErrorMessage && (
                    <p className={styles.errorMessage}>{submitErrorMessage}</p>
                )}
                <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />
            </div>
        );
    };

    return renderContent();
};

export default UpcomingServiceEditorPage;
